import React from 'react';

import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import type {
	PostPreviewSizes,
	RecommendedGroup,
	RecommendedGroupPickerProps,
	SharingProps,
} from '#src/app/pages/starterDashboardV2/shared/components';
import { POST_CONFIGS, type PostConfigRow } from '#src/app/pages/starterDashboardV2/shared/constants';
import { extractSharingResource } from '#src/app/pages/starterDashboardV2/shared/contexts';
import type { useRecommendations } from '#src/app/pages/starterDashboardV2/shared/contexts';
import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';
import { toLinearGradient } from '#src/app/shared/components/ScrollContainer';

import type { DisplayablePost } from '../../hooks';
import { PostPreviewCard } from '../PostPreviewCard';
import { PostPreviewCardRetry } from '../PostPreviewCardRetry';
import { PostPreviewCardSkeleton } from '../PostPreviewCardSkeleton';

const trackingProps = {
	context: 'petition_dashboard_ready_to_share_post' as const,
	viewContext: 'share' as const,
};

function getRecommendedGroupsForPost(
	post: EnrichedReadyToSharePlatformPostV2,
	postConfig: PostConfigRow,
	recommendationsData: ReturnType<typeof useRecommendations>['data'],
) {
	if (!postConfig.requiresRecommendedGroup) return [];
	if (post.platform === 'instagram') return [];

	const { isLoading: isRecommendationsLoading } = recommendationsData;

	if (isRecommendationsLoading) return [];

	return post.platform === 'facebook' ? recommendationsData.facebook : recommendationsData.reddit;
}

type PostOrStoryItemProps = {
	post: EnrichedReadyToSharePlatformPostV2;
	isMobile: boolean;
	petition: StarterDashboardV2PageData['petition'];
	previewSize: PostPreviewSizes;
	startSharingFlow: (props: SharingProps) => void;
	startGroupSelection: (
		props: Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>,
	) => Promise<RecommendedGroup | undefined>;
	trackClick: (post: EnrichedReadyToSharePlatformPostV2) => void;
	trackGroupSelection: (post: EnrichedReadyToSharePlatformPostV2) => void;
	recommendationsData: ReturnType<typeof useRecommendations>['data'];
};

const PostOrStoryItem: React.FC<PostOrStoryItemProps> = ({
	post,
	isMobile,
	petition,
	recommendationsData,
	startSharingFlow,
	startGroupSelection,
	previewSize,
	trackClick,
	trackGroupSelection,
}) => {
	const postConfig = POST_CONFIGS[post.__typename];
	const postDisabled = !isMobile && !postConfig.desktopEnabled;
	const recommendedGroups = getRecommendedGroupsForPost(post, postConfig, recommendationsData);

	const sharingResource = extractSharingResource(post, {
		petition,
		recommendedGroup: recommendedGroups[0],
	});

	const handlePostClick = !postDisabled
		? async () => {
				trackClick(post);

				// instagram does not support group selection
				if (recommendedGroups.length > 0 && post.platform !== 'instagram') {
					// Skip group selection modal when only one group is available
					if (recommendedGroups.length === 1) {
						startSharingFlow({ post, petition, trackingProps, recommendedGroup: recommendedGroups[0] });
						return;
					}

					trackGroupSelection(post);
					const selectedGroup = await startGroupSelection({
						platform: post.platform,
						recommendedGroups,
					});

					if (selectedGroup) {
						startSharingFlow({ post, petition, trackingProps, recommendedGroup: selectedGroup });
					}
					return;
				}

				startSharingFlow({ post, petition, trackingProps });
			}
		: () => {};

	return (
		<PostPreviewCard
			onClick={handlePostClick}
			post={post}
			disabled={postDisabled}
			petition={petition}
			sharingResource={sharingResource}
			previewSize={previewSize}
		/>
	);
};

export type PostsCarouselProps = {
	displayablePosts: DisplayablePost[];
	isMobile: boolean;
	petition: StarterDashboardV2PageData['petition'];
	previewSize: PostPreviewSizes;
	recommendationsData: ReturnType<typeof useRecommendations>['data'];
	startSharingFlow: (props: SharingProps) => void;
	startGroupSelection: (
		props: Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>,
	) => Promise<RecommendedGroup | undefined>;
	trackClick: (post: EnrichedReadyToSharePlatformPostV2) => void;
	trackGroupSelection: (post: EnrichedReadyToSharePlatformPostV2) => void;
	scrollContainerRef: React.RefObject<HTMLDivElement | null>;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	retryStates: Record<EnrichedReadyToSharePlatformPostV2['__typename'], 'idle' | 'retrying' | 'failed'>;
	onRetry: (postTypeName: EnrichedReadyToSharePlatformPostV2['__typename']) => Promise<void>;
};

export const PostsCarousel: React.FC<PostsCarouselProps> = ({
	displayablePosts,
	isMobile,
	petition,
	previewSize,
	recommendationsData,
	startSharingFlow,
	startGroupSelection,
	trackClick,
	trackGroupSelection,
	scrollContainerRef,
	canScrollLeft,
	canScrollRight,
	retryStates,
	onRetry,
}) => {
	return (
		<div className="relative overflow-hidden">
			<div
				className="flex w-full flex-row gap-x-[39px] overflow-x-auto px-1 pt-4 pb-3"
				ref={scrollContainerRef}
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{displayablePosts.map((displayablePost) => {
					const { postTypeName, key, state: postState } = displayablePost;

					if (postState === 'retry') {
						return (
							<PostPreviewCardRetry
								key={key}
								postTypeName={postTypeName}
								petition={petition}
								previewSize={previewSize}
								retryState={retryStates[postTypeName]}
								onRetry={() => {
									void onRetry(postTypeName);
								}}
							/>
						);
					}

					if (postState === 'loaded') {
						return (
							<PostOrStoryItem
								key={key}
								post={displayablePost.post}
								isMobile={isMobile}
								petition={petition}
								previewSize={previewSize}
								recommendationsData={recommendationsData}
								startSharingFlow={startSharingFlow}
								startGroupSelection={startGroupSelection}
								trackClick={trackClick}
								trackGroupSelection={trackGroupSelection}
							/>
						);
					}

					return (
						<PostPreviewCardSkeleton key={key} size={previewSize} petition={petition} postTypeName={postTypeName} />
					);
				})}
			</div>
			<div
				aria-hidden
				className={canScrollLeft ? 'hidden md:block' : 'hidden'}
				sx={{
					position: 'absolute',
					left: 0,
					top: 0,
					bottom: '12px',
					width: '50px',
					pointerEvents: 'none',
					zIndex: 10,
					background: toLinearGradient('#ffffff', 'left'),
				}}
			/>
			<div
				aria-hidden
				className={canScrollRight ? 'hidden md:block' : 'hidden'}
				sx={{
					position: 'absolute',
					right: 0,
					top: 0,
					bottom: '12px',
					width: '50px',
					pointerEvents: 'none',
					zIndex: 10,
					background: toLinearGradient('#ffffff', 'right'),
				}}
			/>
		</div>
	);
};
